/**
 * The spawn picker: search any airport on Earth, pick an end, pick where to start.
 *
 * A placeholder for the real menu, but a *working* one — CLAUDE.md's non-negotiable #6 is that
 * anything stubbed is stubbed working, and "pick an airport" is the feature that makes 11,776
 * airports worth having at all. Opened with `L` (for location), because a
 * simulator that drops you somewhere without saying where is a simulator you cannot check.
 *
 * The search runs against the binary database directly (see `world/airports/db.ts`) — no index
 * to build, no list to materialise. Typing is debounced by one animation frame rather than a
 * timer, so the results keep up with a fast typist without ever running twice for one keystroke.
 *
 * `updateFixed` / `updateFrame`: neither. Pure DOM, driven by events.
 */

import type { AirportDatabase, AirportSummary, Runway } from '../world/airports/db.js';

/** What the pilot chose. */
export interface SpawnChoice {
  icao: string;
  runway: Runway;
  /** Which end of the runway to use. */
  end: 'low' | 'high';
  /** Threshold, or three miles out on final. */
  mode: 'threshold' | 'final';
}

export interface SpawnPickerOptions {
  onSpawn: (choice: SpawnChoice) => void;
  parent?: HTMLElement;
}

export class SpawnPicker {
  readonly element: HTMLDivElement;

  private readonly input: HTMLInputElement;
  private readonly results: HTMLDivElement;
  private readonly detail: HTMLDivElement;
  private readonly onSpawn: SpawnPickerOptions['onSpawn'];

  private database: AirportDatabase | null = null;
  private visibleValue = false;
  private searchQueued = false;
  private selected: { summary: AirportSummary; runways: Runway[] } | null = null;
  private mode: SpawnChoice['mode'] = 'threshold';

  constructor(options: SpawnPickerOptions) {
    this.onSpawn = options.onSpawn;

    this.element = document.createElement('div');
    this.element.className = 'spawn';
    this.element.hidden = true;

    const panel = document.createElement('div');
    panel.className = 'spawn__panel';

    const title = document.createElement('div');
    title.className = 'spawn__title';
    title.textContent = 'Choose an airport';
    panel.append(title);

    this.input = document.createElement('input');
    this.input.className = 'spawn__input';
    this.input.type = 'search';
    this.input.placeholder = 'ICAO or name — KSFO, Innsbruck, Courchevel…';
    this.input.autocomplete = 'off';
    this.input.spellcheck = false;
    panel.append(this.input);

    this.results = document.createElement('div');
    this.results.className = 'spawn__results';
    panel.append(this.results);

    this.detail = document.createElement('div');
    this.detail.className = 'spawn__detail';
    panel.append(this.detail);

    const hint = document.createElement('div');
    hint.className = 'spawn__hint';
    hint.textContent = 'Enter to fly · Esc to close · L to reopen';
    panel.append(hint);

    this.element.append(panel);
    (options.parent ?? document.body).append(this.element);

    this.input.addEventListener('input', () => this.queueSearch());
    this.element.addEventListener('keydown', (event) => this.onKeyDown(event));
    // Clicking the backdrop closes; clicking the panel must not.
    this.element.addEventListener('pointerdown', (event) => {
      if (event.target === this.element) this.setVisible(false);
    });
    panel.addEventListener('pointerdown', (event) => event.stopPropagation());
  }

  /** Hand over the database once it has downloaded. */
  setDatabase(database: AirportDatabase): void {
    this.database = database;
    this.input.placeholder = `ICAO or name — ${database.airportCount.toLocaleString()} airports`;
    if (this.visibleValue) this.runSearch();
  }

  get visible(): boolean {
    return this.visibleValue;
  }

  toggle(): void {
    this.setVisible(!this.visibleValue);
  }

  setVisible(visible: boolean): void {
    this.visibleValue = visible;
    this.element.hidden = !visible;
    if (visible) {
      this.input.focus();
      this.input.select();
      this.runSearch();
    } else {
      this.input.blur();
    }
  }

  /** Pre-fill the box, e.g. with wherever the session started. */
  setQuery(query: string): void {
    this.input.value = query;
  }

  private onKeyDown(event: KeyboardEvent): void {
    // The picker owns the keyboard while it is open; otherwise typing "KSFO" would also
    // deploy the flaps, cycle the view and fire the guns.
    event.stopPropagation();
    if (event.key === 'Escape') {
      this.setVisible(false);
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      this.spawnSelected();
    }
  }

  /**
   * Coalesce keystrokes onto the next frame.
   *
   * A search is a scan of 11,776 records — under a millisecond, but a fast typist generates
   * keystrokes faster than that in bursts, and there is no point searching for a prefix that
   * has already been superseded.
   */
  private queueSearch(): void {
    if (this.searchQueued) return;
    this.searchQueued = true;
    globalThis.requestAnimationFrame(() => {
      this.searchQueued = false;
      this.runSearch();
    });
  }

  private runSearch(): void {
    const database = this.database;
    this.results.replaceChildren();

    if (database === null) {
      this.results.append(message('loading the airport database…'));
      return;
    }

    const query = this.input.value.trim();
    const matches = query === '' ? this.suggestions(database) : database.search(query, 14);

    if (matches.length === 0) {
      this.results.append(message(`nothing matching “${query}”`));
      this.showDetail(null);
      return;
    }

    for (const summary of matches) {
      this.results.append(this.resultRow(database, summary));
    }
    this.select(database, matches[0]);
  }

  /** What to offer before anyone has typed: a handful of airports worth flying. */
  private suggestions(database: AirportDatabase): AirportSummary[] {
    const featured = ['KSFO', 'KJFK', 'LOWI', 'LFLJ', 'EGLL', 'RJTT', 'NZQN', 'LFMN'];
    const out: AirportSummary[] = [];
    for (const icao of featured) {
      const index = database.indexOfIcao(icao);
      if (index < 0) continue;
      const [match] = database.search(icao, 1);
      if (match !== undefined) out.push(match);
    }
    return out;
  }

  private resultRow(database: AirportDatabase, summary: AirportSummary): HTMLElement {
    const row = document.createElement('button');
    row.type = 'button';
    row.className = 'spawn__result';

    const icao = document.createElement('span');
    icao.className = 'spawn__icao';
    icao.textContent = summary.icao;

    const name = document.createElement('span');
    name.className = 'spawn__name';
    name.textContent = summary.name;

    const meta = document.createElement('span');
    meta.className = 'spawn__meta';
    meta.textContent = `${summary.runwayCount} rwy · ${Math.round(summary.longestRunwayM)} m`;

    row.append(icao, name, meta);
    row.addEventListener('click', () => {
      this.select(database, summary);
      this.spawnSelected();
    });
    row.addEventListener('pointerenter', () => this.select(database, summary));
    return row;
  }

  private select(database: AirportDatabase, summary: AirportSummary): void {
    const airport = database.byIcao(summary.icao);
    if (airport === null) return;
    this.selected = { summary, runways: airport.runways };
    this.showDetail(this.selected);

    for (const child of this.results.children) {
      child.classList.toggle('spawn__result--active', child.firstChild?.textContent === summary.icao);
    }
  }

  private showDetail(selection: { summary: AirportSummary; runways: Runway[] } | null): void {
    this.detail.replaceChildren();
    if (selection === null) return;

    const header = document.createElement('div');
    header.className = 'spawn__detailTitle';
    header.textContent =
      `${selection.summary.icao} · ${Math.round(selection.summary.elevationM)} m elevation`;
    this.detail.append(header);

    const ends = document.createElement('div');
    ends.className = 'spawn__ends';
    for (const runway of selection.runways) {
      for (const end of ['low', 'high'] as const) {
        const ident = end === 'low' ? runway.lowIdent : runway.highIdent;
        if (ident === '') continue;
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'spawn__end';
        button.textContent = ident;
        button.title =
          `${Math.round(runway.lengthM)} m · ${runway.surface} · ` +
          `${(end === 'low' ? runway.headingDegTrue : (runway.headingDegTrue + 180) % 360).toFixed(0)}°T`;
        button.addEventListener('click', () => this.onSpawn({
          icao: selection.summary.icao,
          runway,
          end,
          mode: this.mode,
        }));
        ends.append(button);
      }
    }
    this.detail.append(ends);

    const modes = document.createElement('div');
    modes.className = 'spawn__modes';
    for (const [mode, label] of [
      ['threshold', 'On the threshold'],
      ['final', '3 nm final'],
    ] as const) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = mode === this.mode ? 'spawn__mode spawn__mode--active' : 'spawn__mode';
      button.textContent = label;
      button.addEventListener('click', () => {
        this.mode = mode;
        this.showDetail(selection);
      });
      modes.append(button);
    }
    this.detail.append(modes);
  }

  /** Fly to whatever is selected, using its longest runway. */
  private spawnSelected(): void {
    const selection = this.selected;
    if (selection === null || selection.runways.length === 0) return;
    const runway = selection.runways.reduce((best, r) => (r.lengthM > best.lengthM ? r : best));
    this.onSpawn({ icao: selection.summary.icao, runway, end: 'low', mode: this.mode });
  }

  dispose(): void {
    this.element.remove();
  }
}

function message(text: string): HTMLElement {
  const element = document.createElement('div');
  element.className = 'spawn__message';
  element.textContent = text;
  return element;
}
